import { ActionManager, Color3, Color4, ExecuteCodeAction, IAction, IActionEvent, PredicateCondition, Scene } from '@babylonjs/core';
import { Control, Vector2WithInfo } from '@babylonjs/gui/2D';
import { PointCloud } from 'Api/Dto/PointCloud';
import { LocalizerService } from 'Api/Services';
import { LineMeasure, Measurable } from 'App/Areas/Explore/Components/Behaviors/Measure';
import { ISphericalCoordinates } from 'Framework/Components/Controls/XView/Core';
import { ContextMenu } from 'Framework/Components/Controls/XView/XView.ContextMenu';
import { Viewer360 } from 'Framework/Components/Controls/XView/XView.Viewer360';
import { NotifyCollectionChangedAction, NotifyCollectionChangedEventArgs } from 'Framework/Components/EventArgs/NotifyCollectionChangedEventArgs';
import { UIElement } from 'Framework/Components/UIElement';
import { MathHelper } from 'Framework/Helpers/MathHelper';
import { Behavior } from 'Framework/Interactivity/BehaviorOfT';

/**
 * Add point cloud capabilities to a 360° Viewer control
 */
export class PointCloudBehavior extends Behavior<Viewer360> {
    public constructor(
        onPointerPickedAsync: (sphericalCoordinates: ISphericalCoordinates) => Promise<PointCloud>,
        localizerService: LocalizerService
    ) {
        super();

        this._measures = new Array<Measurable>();
        this._actions = new Array<IAction>();

        this._onPointerPickedAsync = onPointerPickedAsync;
        this._pointCloudMeasureRemovedFunc = (o, e) => this._onPointCloudMeasureRemoved(o, e);

        this._localizerService = localizerService;
    }

    public onAttached(): void {
        this._pointCloudScene = this.associatedObject.addNewScene();

        this._registerEvents();
        this._addContextMenuItems(this.associatedObject.contextMenu);

        this._pointCloudScene.activeCamera = this.associatedObject.camera;
        this._pointCloudScene.autoClear = false;
        this._pointCloudScene.registerBeforeRender(() => this._onUpdateScene());
        this._counterMeasure = 1;
    }

    public onDetaching(): void {
        UIElement.Bus.$off('eb-point-cloud-measure-remove', this._pointCloudMeasureRemovedFunc);

        this._measures.forEach(m => m.dispose(false, true));
        this._measures.splice(0);

        this._unRegisterEvents();
        this._removeContextMenuItems();

        UIElement.Bus.$emit(
            'eb-point-cloud-measure-changed',
            this,
            NotifyCollectionChangedEventArgs.createReset()
        );

        this._pointCloudScene.dispose();
    }

    private _registerEvents(): void {
        const actionManager = this.rendererScene.actionManager as ActionManager;

        this._actions.push(
            actionManager.registerAction(new ExecuteCodeAction(
                ActionManager.OnLongPressTrigger,
                e => this._showContextMenu(e)
            )),
            actionManager.registerAction(new ExecuteCodeAction(
                ActionManager.OnRightPickTrigger,
                e => this._showContextMenu(e)
            )),
            actionManager.registerAction(new ExecuteCodeAction(
                ActionManager.OnPickDownTrigger,
                e => {
                    this.associatedObject.contextMenu.close();
                },
                new PredicateCondition(
                    actionManager,
                    () => this.associatedObject.contextMenu.isOpen
                )
            )),
            actionManager.registerAction(new ExecuteCodeAction(
                {
                    trigger: ActionManager.OnKeyUpTrigger,
                    parameter: 27 //Escape
                },
                e => {
                    if (this.associatedObject.contextMenu.isOpen) {
                        this.associatedObject.contextMenu.close();

                        return;
                    }

                    this._tryCancelCurrentMeasure();
                }
            ))
        );

        UIElement.Bus.$on('eb-point-cloud-measure-remove', this._pointCloudMeasureRemovedFunc);
    }

    private _unRegisterEvents(): void {
        this._actions.forEach(a => this.rendererScene.actionManager.unregisterAction(a));
        this._actions.splice(0);
    }

    private _addContextMenuItems(contextMenu: ContextMenu): void {
        this._contextMenuItems = Array.from(contextMenu.addMenuItems(
            {
                id: 'PickMeasureButtonStart',
                buttonType: 'Normal',
                text: this._localizerService.localize(`${this._translationBasePath}.PickMeasureButtonStart`),
                background: Color3.FromHexString('#272727'),
                foreground: Color3.White(),
            },
            {
                id: 'PickMeasureButtonEnd',
                buttonType: 'Normal',
                text: this._localizerService.localize(`${this._translationBasePath}.PickMeasureButtonEnd`),
                background: Color3.FromHexString('#272727'),
                foreground: Color3.White(),
                isVisible: false
            }
        ));

        const [pickMeasureButtonStart, pickMeasureButtonEnd] = this._contextMenuItems;

        const pickingMeasure = async (_: Vector2WithInfo) => {
            await this._pick2DMeasureAsync(contextMenu.openAt.x, contextMenu.openAt.y);

            contextMenu.close();
        };

        pickMeasureButtonStart.onPointerClickObservable.add(pickingMeasure);
        pickMeasureButtonEnd.onPointerClickObservable.add(pickingMeasure);
    }

    private _removeContextMenuItems(): void {
        this.associatedObject.contextMenu.removeMenuItems(this._contextMenuItems);
    }

    private _switchContextMenuButtons(): void {
        const [pickMeasureButtonStart, pickMeasureButtonEnd] = this._contextMenuItems;

        const isEndVisible: boolean = this._currentMeasure?.pointCount == 1;

        pickMeasureButtonStart.isVisible = !isEndVisible;
        pickMeasureButtonEnd.isVisible = isEndVisible;
    }

    private _showContextMenu(e: IActionEvent): void {
        this._switchContextMenuButtons();
        this.associatedObject.contextMenu.showAt(e.pointerX, e.pointerY);
    }

    private _removeMeasure(deletable: Measurable): boolean {
        let index = this._measures.indexOf(deletable);

        if (index == -1) {
            return false;
        }

        const [removed] = this._measures.splice(index, 1);
        removed.dispose(false, true);

        return true;
    }

    private _onUpdateScene(): void {
        this._measures.forEach(m => m.update());
    }

    private _tryCancelCurrentMeasure(): void {
        if (!this._currentMeasure?.canAddPoint()) {
            return;
        }

        if (this._removeMeasure(this._currentMeasure)) {
            UIElement.Bus.$emit(
                'eb-point-cloud-measure-changed',
                this,
                NotifyCollectionChangedEventArgs.createOneItemChange(
                    NotifyCollectionChangedAction.Remove,
                    this._currentMeasure
                )
            );

            this._currentMeasure = null;
        }
    }

    private async _pick2DMeasureAsync(pickingX: number, pickingY: number): Promise<void> {
        const pickInfo = this.associatedObject.pick(pickingX, pickingY);

        if (!pickInfo.hit) {
            return;
        }

        const sphericalCoords = MathHelper.toSphericalCoordinates(pickInfo);

        let cartesianCoords: PointCloud = await this._onPointerPickedAsync(sphericalCoords);

        if (!cartesianCoords) {
            return;
        }

        this._createMeasureIfNotExist();

        this._currentMeasure.addPoint(pickInfo.pickedPoint, cartesianCoords);

        if (this._currentMeasure.pointCount == 1) {
            UIElement.Bus.$emit(
                'eb-point-cloud-measure-changed',
                this,
                NotifyCollectionChangedEventArgs.createOneItemChange(NotifyCollectionChangedAction.Add, this._currentMeasure)
            );
        }
        else if (this._currentMeasure.pointCount == 2) {
            this._counterMeasure++;

            UIElement.Bus.$emit(
                'eb-point-cloud-measure-changed',
                this,
                NotifyCollectionChangedEventArgs.createOneItemReplace(this._currentMeasure, this._currentMeasure)
            );

            (this._currentMeasure as LineMeasure).hideGhostLine();
            this._currentMeasure = null;
        }
    }

    private _createMeasureIfNotExist(): void {
        if (this._currentMeasure == null) {
            let counter = this._counterMeasure;

            let line = new LineMeasure(`LineMeasure${counter}`, this._pointCloudScene);

            line.startPointColor = Color3.Green();
            line.endPointColor = Color3.Red();
            line.textColor = new Color4(1, 0, 1, 1);
            line.backgroundTextColor = new Color4(0, 0, 0, 0);

            line.formatText = (length: number) => {
                let localizedKey = null;
                let params = new Array<string | number>();

                if (Number.isNaN(length)) {
                    if (line.canAddPoint()) {
                        localizedKey = 'PickingMeasurementFormat';
                        params.push(counter);
                    }
                    else {
                        localizedKey = 'InvalidMeasurement';
                    }
                }
                else {
                    localizedKey = 'PickedMeasurementFormat';
                    params.push(counter, length.toFixed(2));
                }

                return this._localizerService.localize(
                    `JS.Plugin.MeasureTool.${localizedKey}`,
                    ...params
                );
            }

            this.rendererScene.onPointerObservable.add(e => {
                line.showGhostLineAt(e.pickInfo.pickedPoint);
            });

            this._currentMeasure = line;
            this._measures.push(this._currentMeasure);
        }
    }

    private _onPointCloudMeasureRemoved(_: UIElement, e: NotifyCollectionChangedEventArgs): void {
        let [remove] = e.oldItems;

        if (this._removeMeasure(remove) && remove == this._currentMeasure) {
            this._currentMeasure = null;
        }

        if (this._measures.length == 0) {
            this._counterMeasure = 1;
        }
    }

    public get measures(): Array<Measurable> {
        return this._measures;
    }

    public get rendererScene(): Scene {
        return this.associatedObject.scenes.find(s => s.metadata?.isRenderer);
    }

    private _pointCloudScene: Scene;
    private _currentMeasure: Measurable;
    private _counterMeasure: number;
    private _contextMenuItems: Array<Control>;
    private readonly _actions: Array<IAction>;
    private readonly _measures: Array<Measurable>;
    private readonly _translationBasePath: string = 'JS.Framework.Controls.XView.ContextMenu';
    private readonly _onPointerPickedAsync: (sphericalCoordinates: ISphericalCoordinates) => Promise<PointCloud>;
    private readonly _pointCloudMeasureRemovedFunc: (sender: UIElement, e: NotifyCollectionChangedEventArgs) => void;

    private readonly _localizerService: LocalizerService;
}
